import { useContext } from "react";
import { PageContext } from "./page";
import type { Words } from "./copy";
import { BasketContext, pieceKey } from "./selection";
import type { TogetherPiece } from "./OrderAll";
import { track } from "../track";

/** The pieces of a page of several as a row of small pictures, held at the top
 *  of the screen while the cards go by: which they are, which are in the order
 *  (a tick), and a tap on one goes to its card. With three cards a screen
 *  tall each, it is the only overview the page has.
 *
 *  Each is the piece's own drawing, or the photo it was drawn from, with its
 *  place on the page. The cards are anchored by their id (`piece-…`, Preview).
 *  Sticky rather than fixed, so the keyboard pushes it. */
export default function PieceRail({ pieces, words: t }: { pieces: TogetherPiece[]; words: Words }) {
  const info = useContext(BasketContext);
  const page = useContext(PageContext);
  // Paid for: nothing left to choose, as the summary and the bar say.
  if (!info?.on || (page?.ordered && !page.admin)) return null;
  const included = new Set(info.basket.lines.map((l) => l.key));

  return (
    <nav className="rail" aria-label={t.t("chooseYours")}>
      {pieces.map((p) => {
        const key = pieceKey(p.token, p.piece.id);
        const src = p.drawingUrl ?? p.piece.photoUrl ?? null;
        const on = included.has(key);
        return (
          <button
            key={key}
            type="button"
            className={on ? "rail-item rail-on" : "rail-item"}
            aria-label={`${p.at + 1} / ${p.of}${on ? ` · ${t.t("basketIn")}` : ""}`}
            onClick={() => {
              track("rail.jump", { at: p.at + 1 });
              document.getElementById(`piece-${p.piece.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {src ? (
              <img
                src={src}
                alt=""
                loading="lazy"
                /* A drawing wider than it is tall (a row of three or four pets
                   alone, 3:2) is shown whole: cropped to the square, only the
                   middle pets would be left. A photo stays cropped. */
                onLoad={(event) =>
                  event.currentTarget.classList.toggle(
                    "rail-wide",
                    !!p.drawingUrl && event.currentTarget.naturalWidth > event.currentTarget.naturalHeight
                  )
                }
              />
            ) : (
              <span className="rail-blank" />
            )}
            <span className="rail-n mono">{p.at + 1}</span>
            {on && <span className="rail-tick" aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </nav>
  );
}
